import { Link } from 'react-router-dom';

import { Button } from '../shared/ui/Button';

import s from './HomePage.module.css';

export function HomePage(): JSX.Element {
  return (
    <div className={s.root}>
      <div className={s.bg} aria-hidden="true" />

      <nav className={s.topnav} aria-label="Marketing">
        <Link to="/" className={s.brand}>
          <span className={s.mark} aria-hidden="true">
            i
          </span>
          IELTS Mock
        </Link>
        <div className={s.topnavLinks}>
          <Link to="/register-institute" className={s.navLink}>
            For institutes
          </Link>
          <Link to="/login" className={s.navLink}>
            Log in
          </Link>
          <Link to="/signup" className={s.navCta}>
            Get started
          </Link>
        </div>
      </nav>

      <header className={s.hero}>
        <span className={s.eyebrow}>
          <span className={s.eyebrowDot} aria-hidden="true" />
          Built for institutes that take prep seriously
        </span>
        <h1 className={s.headline}>IELTS Mock Platform</h1>
        <p className={s.tagline}>
          The exam-true practice{' '}
          <span className={s.headlineAccent}>your institute can stand behind.</span>
        </p>
        <p className={s.subhead}>
          Practice IELTS Academic and General Training mocks with real timing, real feedback, and
          AI-assisted scoring across Listening, Reading, Writing, and Speaking.
        </p>
        <div className={s.actions}>
          <Link to="/signup">
            <Button>Get started — it's free</Button>
          </Link>
          <Link to="/login">
            <Button variant="secondary">Log in</Button>
          </Link>
        </div>
        <p className={s.instituteCta}>
          Running a coaching centre?{' '}
          <Link to="/register-institute" className={s.instituteLink}>
            Register your institute →
          </Link>
        </p>
        <div className={s.trust}>
          <span>Real exam timing</span>
          <span>·</span>
          <span>AI band feedback</span>
          <span>·</span>
          <span>Institute dashboards</span>
          <span>·</span>
          <span>Realtime speaking</span>
        </div>
      </header>

      <section className={s.preview} aria-label="Product preview">
        <div className={s.previewCard}>
          <div className={s.previewHeader}>
            <span className={s.windowDot} />
            <span className={s.windowDot} />
            <span className={s.windowDot} />
            <span className={s.timer}>
              <span className={s.timerDot} aria-hidden="true" />
              Reading · 32:14
            </span>
          </div>
          <div className={s.previewBody}>
            <div className={s.passage}>
              <h3>The Future of Renewable Energy</h3>
              <p>
                The transition toward renewable energy sources has accelerated dramatically over the
                past decade. <span className={s.highlight}>Solar and wind capacity</span> has more
                than tripled, while costs have fallen to a fraction of their previous levels.
              </p>
              <p>
                Researchers argue that the coming years will determine whether the global grid can
                keep pace with demand without compromising the climate goals set in 2015.
              </p>
            </div>
            <div className={s.questions}>
              <div className={s.q}>
                <span className={s.qNum}>Question 14 · Multiple choice</span>
                <span className={s.qText}>According to the passage, renewable capacity has:</span>
                <div className={s.options}>
                  <span className={s.opt}>
                    <span className={s.optBullet}>A</span> declined steadily
                  </span>
                  <span className={`${s.opt} ${s.optSelected}`}>
                    <span className={s.optBullet}>B</span> more than tripled
                  </span>
                  <span className={s.opt}>
                    <span className={s.optBullet}>C</span> remained flat
                  </span>
                  <span className={s.opt}>
                    <span className={s.optBullet}>D</span> halved
                  </span>
                </div>
              </div>
              <div className={s.q}>
                <span className={s.qNum}>Question 15 · True / False / Not given</span>
                <span className={s.qText}>
                  The 2015 climate goals are mentioned as a benchmark.
                </span>
                <div className={s.options}>
                  <span className={`${s.opt} ${s.optSelected}`}>
                    <span className={s.optBullet}>✓</span> True
                  </span>
                  <span className={s.opt}>
                    <span className={s.optBullet}>✗</span> False
                  </span>
                  <span className={s.opt}>
                    <span className={s.optBullet}>?</span> Not given
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={s.featureGrid} aria-label="Features">
        <article className={s.feature}>
          <span className={s.featureIcon} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="9" cy="9" r="7.25" />
              <path d="M9 4.5V9l2.5 2.5" />
            </svg>
          </span>
          <h3 className={s.featureTitle}>Exam-true timing</h3>
          <p className={s.featureBody}>
            Section timers, transfer time, and audio playback rules mirror the real IELTS test — so
            practice feels like exam day.
          </p>
        </article>
        <article className={s.feature}>
          <span className={s.featureIcon} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2.25 13.5 7.5 8.25l3 3 5.25-5.25" />
              <path d="M11.25 6h4.5v4.5" />
            </svg>
          </span>
          <h3 className={s.featureTitle}>AI band feedback</h3>
          <p className={s.featureBody}>
            Writing and Speaking are scored against IELTS band descriptors with sentence-level
            rationale your tutors can review.
          </p>
        </article>
        <article className={s.feature}>
          <span className={s.featureIcon} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 14.25v-1.5a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v1.5" />
              <circle cx="9" cy="5.25" r="2.75" />
            </svg>
          </span>
          <h3 className={s.featureTitle}>Institute mode</h3>
          <p className={s.featureBody}>
            Add students, batches, and teachers. Track progress, audit grading, and bill per mock —
            built for IELTS coaching centres.
          </p>
        </article>
        <article className={s.feature}>
          <span className={s.featureIcon} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 1.5v3M9 13.5v3M3.4 3.4l2.1 2.1M12.5 12.5l2.1 2.1M1.5 9h3M13.5 9h3M3.4 14.6l2.1-2.1M12.5 5.5l2.1-2.1" />
            </svg>
          </span>
          <h3 className={s.featureTitle}>Realtime speaking</h3>
          <p className={s.featureBody}>
            Live speaking sessions with low-latency audio, interruption handling, and on-the-fly
            transcripts ready for grading.
          </p>
        </article>
      </section>

      <footer className={s.foot}>
        <span>© IELTS Mock Platform</span>
        <span>Built for institutes that take preparation seriously.</span>
      </footer>
    </div>
  );
}
